import React, { useContext, useEffect, useState, useCallback } from "react";
import { toast } from "@bfc-software/pallet";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AuthContext,
  AuthUtils,
  AUTHENTICATED,
  AUTH_ERROR,
  AWAITING_PASSWORD,
  LOADING_ORGS,
  AWAITING_ORG_SELECT,
  CREATING_SESSION,
  AUTHENTICATING,
} from "@bfc-software/app-toolbox";

import EmailEntry from "./-components/email-entry";
import PasswordEntry from "./-components/password-entry";
import OrganizationSelect from "./-components/organization-select";
import AuthContainer from "./-components/auth-container";
import RequestOrgs from "./-components/request-orgs";
import styles from "./login.module.css";

type LoginQueryParams = {
  sPL?: "true";
};

type ParsedLoginParams = {
  sPL?: "true";
};

export const Route = createFileRoute("/$lang/_layout/auth/login")({
  component: Login,
  validateSearch: (search: LoginQueryParams): ParsedLoginParams => {
    if (search.sPL && search.sPL === "true") {
      return { sPL: search.sPL };
    }
    return {};
  },
});

export default function Login() {
  const params = Route.useParams();
  const { state, startLogin, selectOrg, submitPassword } =
    useContext(AuthContext);
  const navigate = useNavigate();
  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.login,
  });

  // Local UI state for flows the state machine doesn't own
  const [flow, setFlow] = useState<"login" | "reset-password">("login");
  const [resetOrgId, setResetOrgId] = useState("");

  // Navigate when authenticated
  useEffect(() => {
    if (state.status === AUTHENTICATED) {
      const orgSlug = AuthUtils.getOrgSlug(state, state.orgId);

      navigate({
        // The tile screen. Which tool the user wants is their choice, not a
        // default baked into the login flow.
        to: "/$lang/orgs/$org",
        params: {
          lang: params.lang,
          org: orgSlug,
        },
      });
    }
  }, [state, navigate, params.lang]);

  // Show error toast
  useEffect(() => {
    if (state.status === AUTH_ERROR) {
      toast.error(
        <div className={styles.errorToast}>
          <p className={styles.heading}>{t("errorNotification.message")}</p>
          <p className={styles.errorMessage}>{state.message}</p>
        </div>,
      );
    }
  }, [state, t]);

  const handlePasswordSubmit = useCallback(
    (ev: React.FormEvent) => {
      ev.preventDefault();
      if (state.status === AWAITING_PASSWORD) {
        const passwordInput = (ev.target as HTMLFormElement).elements.namedItem(
          "password",
        ) as HTMLInputElement | null;
        if (passwordInput?.value) {
          submitPassword(passwordInput.value);
        }
      }
    },
    [state.status, submitPassword],
  );

  let view;
  switch (state.status) {
    case LOADING_ORGS: {
      view = (
        <RequestOrgs
          lang={params.lang}
          email={state.email}
          goBack={{
            label: t("goBack.toEmail"),
            shouldRender: true,
            handler: () => {
              // Go back to email entry by logging out (resets to unauthenticated)
              // This is a soft reset, not a full logout
            },
          }}
        />
      );
      break;
    }

    case AWAITING_ORG_SELECT: {
      view = (
        <OrganizationSelect
          email={state.email}
          flow={flow}
          orgId={resetOrgId}
          setParentForm={({ orgId, flow: newFlow }) => {
            if (newFlow) setFlow(newFlow);
            if (orgId) {
              if (flow === "reset-password" || newFlow === "reset-password") {
                // Don't advance the state machine - just track the org for the reset email
                setResetOrgId(orgId);
              } else {
                selectOrg(orgId);
              }
            }
          }}
          orgs={state.orgs}
          lang={params.lang}
          goBack={{
            label: t("goBack.toEmail"),
            shouldRender: true,
            handler: () => {
              setResetOrgId("");
              setFlow("login");
            },
          }}
        />
      );
      break;
    }

    case AWAITING_PASSWORD: {
      const locationName =
        state.orgs.length > 0
          ? (() => {
              const locations = AuthUtils.getLocations(state, state.orgId);
              return locations.length === 1
                ? (locations[0]?.locationName ?? "")
                : "";
            })()
          : "";

      view = (
        <PasswordEntry
          locationName={locationName}
          orgId={state.orgId}
          lang={params.lang}
          email={state.email}
          goBack={{
            label: t("goBack.toOrganization"),
            shouldRender: true,
            handler: () => {
              // Would need a "go back" action on the state machine
            },
          }}
          setForm={() => {}}
          handleSubmit={handlePasswordSubmit}
        />
      );
      break;
    }

    case AUTHENTICATING: {
      view = (
        <RequestOrgs
          lang={params.lang}
          email={state.email}
          goBack={{ label: "", shouldRender: false, handler: () => {} }}
        />
      );
      break;
    }

    case CREATING_SESSION: {
      // Signing in mints a session with no warehouse, so there is usually no
      // name to show here. It appears only when switching warehouse re-mints.
      const locationName = state.locationId
        ? (AuthUtils.getLocationName(state, state.orgId, state.locationId) ??
          "")
        : "";
      view = (
        <div className={styles.loading}>
          <p>{locationName}</p>
        </div>
      );
      break;
    }

    default: {
      view = (
        <EmailEntry
          showPasswordLogin={true}
          lang={params.lang}
          setParentForm={({ email, flow: newFlow }) => {
            if (newFlow) setFlow(newFlow);
            if (email) startLogin(email);
          }}
        />
      );
    }
  }

  return <AuthContainer>{view}</AuthContainer>;
}
